/**
 * Failure-screen store.
 *
 * Drives the app-wide `GlobalFailureScreen` host. Set from the QueryClient
 * cache callback (`src/api/serverErrorRouting.ts`) when a screen-breaking 5xx
 * occurs — a query that has never succeeded, so the screen has nothing to
 * render. A zustand store so it is reachable from outside React.
 *
 * The error boundaries render `FailureScreen` directly; this store is only
 * for the non-boundary path (a failed query does not unmount anything).
 */
import { create } from 'zustand';
import { registerStoreReset } from './registry';

export interface FailureDetails {
  /** Trace id shown to the user and correlatable to telemetry. */
  traceId: string;
  /** Copy-pasteable error report (buildErrorReport output). */
  report?: string;
}

interface FailureState {
  failure: FailureDetails | null;
  show: (failure: FailureDetails) => void;
  clear: () => void;
}

export const useFailureStore = create<FailureState>((set) => ({
  failure: null,
  show: (failure) => set({ failure }),
  clear: () => set({ failure: null }),
}));

// A failure screen is not about which login the person is: a switch leaves it (#1336).
registerStoreReset(() => useFailureStore.setState({ failure: null }), { onUserSwitch: false });
